// ── participant-certificate-collection: own certificate rows ───────────────

import { useMemo } from "react";
import {
  Badge,
  useCollection,
  type CollectionColumnDef,
  type UseCollectionResult
} from "@vjal/ui";

import type { CoachingCertificateDto } from "../../services/coaching-readiness-dtos";
import { certificateStatusTone, hasCertificateArtifact, orDash } from "../certificates/certificates-helpers";

export const PARTICIPANT_CERTIFICATE_PAGE_SIZE = 25;
export const PARTICIPANT_CERTIFICATE_DEFAULT_SORT = { id: "issuedAt", dir: "desc" as const };

export type ParticipantCertificateRow = CoachingCertificateDto & Record<string, unknown>;

export function buildParticipantCertificateCollectionColumns(): CollectionColumnDef<ParticipantCertificateRow>[] {
  return [
    {
      id: "id",
      header: "Certificate",
      sortable: true,
      sortAccessor: (row) => row.id,
      priority: 1,
      cell: (row) => <span className="font-medium text-strong">#{row.id}</span>
    },
    {
      id: "status",
      header: "Status",
      sortable: true,
      sortAccessor: (row) => row.status ?? "",
      priority: 2,
      cell: (row) => (
        <Badge variant={certificateStatusTone(row.status) === "danger" ? "danger" : certificateStatusTone(row.status)}>
          {row.status}
        </Badge>
      )
    },
    {
      id: "issuedAt",
      header: "Issued",
      sortable: true,
      sortAccessor: (row) => row.issuedAt ?? "",
      priority: 2,
      cell: (row) => <span className="text-sm text-ink-muted">{orDash(row.issuedAt)}</span>
    },
    {
      id: "artifactRef",
      header: "Document",
      sortable: false,
      priority: 2,
      cell: (row) =>
        hasCertificateArtifact(row) ? (
          <Badge variant="success">Document ready</Badge>
        ) : (
          <Badge variant="warning">Document pending</Badge>
        )
    }
  ];
}

export function useParticipantCertificateCollection(
  rows: ParticipantCertificateRow[]
): UseCollectionResult<ParticipantCertificateRow> {
  const columns = useMemo(() => buildParticipantCertificateCollectionColumns(), []);

  return useCollection<ParticipantCertificateRow>({
    mode: "client",
    rows,
    columns,
    pageSize: PARTICIPANT_CERTIFICATE_PAGE_SIZE,
    urlSync: false,
    initialState: { sort: PARTICIPANT_CERTIFICATE_DEFAULT_SORT },
    getRowKey: (row) => row.id
  });
}